import {
  ThirdPartyVendorCategory,
  ThirdPartyVendorControl,
} from "../../../../redux/type";

export const VENDOR_CATEGORY_TABS: {
  label: string;
  value: ThirdPartyVendorCategory;
}[] = [
  { label: "Wallet Vendors", value: "wallet" },
  { label: "Transaction Vendors", value: "transaction" },
  { label: "Exchange rate Vendors", value: "exchange_rate" },
];

/** A single currency's on/off state for both the Business Users and Direct Users rows. */
export interface CurrencyToggleState {
  code: string;
  business: boolean;
  direct: boolean;
}

export interface VendorEditState {
  applies_to_mobile: boolean;
  applies_to_web: boolean;
  mobile_is_active: boolean;
  web_is_active: boolean;
  is_active: boolean;
  currencies: CurrencyToggleState[];
}

/** Body for `PUT /admin/third-party-vendor-controls/{id}` — name/config/supported_currencies/has_wallet are intentionally left out. */
export interface UpdateVendorControlPayload {
  applies_to_mobile: boolean;
  applies_to_web: boolean;
  mobile_is_active: boolean;
  web_is_active: boolean;
  status: "active" | "inactive";
  business_currencies: string[];
  direct_currencies: string[];
}

/**
 * The Business/Direct rows only ever show currencies the vendor actually
 * supports — `business_currencies`/`direct_currencies` are subsets of
 * `supported_currencies`, so that's the sole source of the row's currency
 * list. There is no way to grow this list from the currency toggle UI.
 */
export function buildCurrencyToggles(
  vendor: ThirdPartyVendorControl,
): CurrencyToggleState[] {
  const supported = Array.from(
    new Set(
      (vendor?.supported_currencies || [])
        .filter(Boolean)
        .map((code) => code.toUpperCase()),
    ),
  );
  const businessSet = new Set(
    (vendor?.business_currencies || [])
      .filter(Boolean)
      .map((code) => code.toUpperCase()),
  );
  const directSet = new Set(
    (vendor?.direct_currencies || [])
      .filter(Boolean)
      .map((code) => code.toUpperCase()),
  );

  return supported.map((code) => ({
    code,
    business: businessSet.has(code),
    direct: directSet.has(code),
  }));
}

export function buildVendorEditState(
  vendor: ThirdPartyVendorControl,
): VendorEditState {
  return {
    applies_to_mobile: !!vendor?.applies_to_mobile,
    applies_to_web: !!vendor?.applies_to_web,
    mobile_is_active: !!vendor?.mobile_is_active,
    web_is_active: !!vendor?.web_is_active,
    is_active: vendor?.status === "active",
    currencies: buildCurrencyToggles(vendor),
  };
}

/** Currency codes are lower-cased to match the casing the API returns them in (e.g. "usd"). */
export function buildUpdateVendorPayload(
  editState: VendorEditState,
): UpdateVendorControlPayload {
  return {
    applies_to_mobile: editState.applies_to_mobile,
    applies_to_web: editState.applies_to_web,
    mobile_is_active: editState.mobile_is_active,
    web_is_active: editState.web_is_active,
    status: editState.is_active ? "active" : "inactive",
    business_currencies: editState.currencies
      .filter((currency) => currency.business)
      .map((currency) => currency.code.toLowerCase()),
    direct_currencies: editState.currencies
      .filter((currency) => currency.direct)
      .map((currency) => currency.code.toLowerCase()),
  };
}

function capitalizeWords(value: string): string {
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** `name` is the human-facing label; a handful of vendors (e.g. newly-added ones) only have `vendor_name` set. */
export function vendorDisplayName(
  vendor: ThirdPartyVendorControl | null | undefined,
): string {
  if (vendor?.name) return vendor.name;
  if (vendor?.vendor_name) return capitalizeWords(vendor.vendor_name);
  return "Unnamed Vendor";
}
